import { type ThemeColors } from "../lib/theme";
import type { ReactNode, MouseEvent } from "react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface TargetBadgeProps {
  article: {
    id: number;
    title: string;
    description: string | null;
  };
  isFound: boolean;
  theme: ThemeColors;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  isDark?: boolean;
}

export function TargetBadge({
  article,
  isFound,
  theme,
  children,
  onClick,
  className = "",
  isDark = false,
}: TargetBadgeProps) {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1023px)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const checkMobile = () => setIsMobile(mediaQuery.matches);

    checkMobile();
    mediaQuery.addEventListener("change", checkMobile);
    return () => mediaQuery.removeEventListener("change", checkMobile);
  }, []);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (isMobile && article.description) {
      setShowDialog(true);
    }
    onClick?.(e);
  };

  const truncatedDescription =
    article.description && article.description.length > 1000
      ? article.description.slice(0, 1000) + "..."
      : article.description;

  const content = (
    <>
      <span className="flex items-center gap-2.5 min-w-0">
        <span
          className={`flex-shrink-0 w-1.5 h-1.5 rounded-full transition-all duration-300 ${
            isFound ? "bg-zinc-500" : theme.bgAccent
          }`}
        />
        <span className="truncate">{article.title}</span>
      </span>
      {children && (
        <span className="flex-shrink-0 text-xs tabular-nums opacity-70">
          {children}
        </span>
      )}
    </>
  );

  const tooltipContent = truncatedDescription && (
    <span
      className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden lg:group-hover:block z-50 w-80 p-4 ${theme.card} ${theme.text} text-sm rounded-xl shadow-xl border ${theme.cardHover}`}
    >
      {truncatedDescription}
    </span>
  );

  const baseClasses = `group relative flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 w-full ${className}`;

  const stateClasses = isFound
    ? `${theme.textSecondary} ${theme.bg} ${theme.borderSubtle} border`
    : `${theme.text} ${theme.bgSecondary} border-zinc-400 border`;

  const dialogContent = showDialog && isMobile && (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 cursor-pointer"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`target-title-${article.id}`}
      onClick={() => setShowDialog(false)}
    >
      <div
        className={`w-full max-w-sm rounded-2xl border shadow-2xl animate-in zoom-in-95 duration-200 ${
          isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                isDark ? "bg-zinc-800 text-white" : "bg-gray-100 text-gray-900"
              }`}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3
                id={`target-title-${article.id}`}
                className={`text-lg font-semibold mb-1 ${isDark ? "text-white" : "text-zinc-900"}`}
              >
                {article.title}
              </h3>
              {article.description && (
                <p
                  className={`text-sm leading-relaxed ${isDark ? "text-zinc-400" : "text-zinc-600"} overflow-scroll h-96`}
                >
                  {truncatedDescription}
                </p>
              )}
            </div>
          </div>
        </div>
        <div
          className={`px-6 py-4 border-t flex justify-end ${isDark ? "border-zinc-800" : "border-zinc-100"}`}
        >
          <button
            onClick={() => setShowDialog(false)}
            className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
              isDark
                ? "text-zinc-400 hover:text-white hover:bg-zinc-800"
                : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
            }`}
          >
            {t("components.targetBadge.close")}
          </button>
        </div>
      </div>
    </div>
  );
  const dialog =
    dialogContent && typeof document !== "undefined"
      ? createPortal(dialogContent, document.body)
      : null;

  if (onClick || isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          className={`${baseClasses} ${stateClasses} cursor-pointer active:scale-[0.98]`}
        >
          {content}
          {tooltipContent}
        </button>
        {dialog}
      </>
    );
  }

  return (
    <span className={`${baseClasses} ${stateClasses}`}>
      {content}
      {tooltipContent}
    </span>
  );
}
